import { type ButtonHTMLAttributes, type Ref } from 'react';
import { Waiting } from './Waiting';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** React 19 passes a ref as a prop; it reaches the `<button>` with the rest, for a focus move. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * F-0579 ACC-6 (`docs/concept-colour-accents.md`): each button names its variant as `evx-button-…`,
 * and `index.css` adds Colour's highlight to it — the icon in the frame blue, a 2 px frame hairline
 * along the bottom on hover, the frame-blue focus ring. Never a fill; danger gets nothing.
 */
export const ACCENT_MARKER = 'evx-button-';

const variantClasses: Record<Variant, string> = {
  /*
    Ruling 4 (ASF-19): a dark neutral on white in the light theme, a light neutral under a near-black
    label in the dark one (the `--evx-primary-*` tokens), so the primary stands apart from a dialog and
    from a secondary beside it in both. Never a colour (ACC-5).
  */
  primary:
    'bg-gray-800 text-white shadow-sm hover:bg-gray-950 active:bg-black focus-visible:ring-focus dark:bg-[var(--evx-primary-fill)] dark:text-[var(--evx-on-primary)] dark:hover:bg-[var(--evx-primary-fill-hover)] dark:active:bg-[var(--evx-primary-fill-active)]',
  /*
    I-0869: in the dark theme a secondary button is a neutral step above whatever it sits on — a
    translucent white, so the one token reads as one step up on a #171717 pane, a #242424 card and a
    black code block alike — with a hairline edge. It was `bg-gray-900`, a blue-black darker than the
    cards, which read as a navy fill. Its label is at least 4.5:1 on every dark card
    (`Button.test.tsx` › *a dark secondary button*).
  */
  secondary:
    'bg-gray-100 text-gray-700 shadow-sm hover:bg-gray-200 active:bg-gray-300 focus-visible:ring-focus dark:bg-white/10 dark:text-neutral-200 dark:shadow-[inset_0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:bg-white/15 dark:active:bg-white/20',
  danger:
    'select-none bg-[var(--evx-danger-fill)] text-[var(--evx-on-danger)] shadow-sm hover:bg-[var(--evx-danger-fill-hover)] active:bg-[var(--evx-danger-fill-active)] focus-visible:ring-red-500',
  ghost:
    'text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 focus-visible:ring-focus dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white dark:active:bg-gray-700',
};

/*
  Every size is a 44 px target below `sm`, where the pointer is a finger (I-0653 for `lg`; the lead's
  ruling on F-0229 for `sm`, which was 40 px on a phone on every screen). From `sm` up each keeps its
  compact desktop height.

  I-1294: the 44 is a floor in px (`min-h-touch`), not a height in rem. As a rem height every button
  on a phone was 137 px tall at the largest text size, three times its label. As a floor it is 44 px
  at the default size and as tall as its label needs at any other.
*/
const sizeClasses: Record<Size, string> = {
  sm: 'max-sm:min-h-touch gap-1.5 rounded-md px-3 text-[13px] sm:h-7 sm:px-2.5 [[data-modal-panel]_&]:min-h-touch sm:[[data-modal-panel]_&]:min-h-[28px]',
  md: 'max-sm:min-h-touch gap-1.5 rounded-md px-3 text-[13px] sm:h-8 [[data-modal-panel]_&]:min-h-touch sm:[[data-modal-panel]_&]:min-h-[32px]',
  lg: 'max-sm:min-h-touch gap-2 rounded-lg px-4 text-sm sm:h-10 [[data-modal-panel]_&]:min-h-touch sm:[[data-modal-panel]_&]:min-h-[40px]',
};

/*
  I-1294 (CFC-f3b): and as wide. Where a finger presses (under `sm`, and in a dialog there) a button
  is 44 px across as well as up: an icon alone in it was its glyph and its padding, and the Activity
  toolbar's Refresh measured 42 × 44 on a phone. A label wider than that keeps its own width. A
  caller's width (`w-*`, `min-w-*`) drops it, as a caller's height drops the height floor.
*/
const WIDTH_FLOOR = 'max-sm:min-w-touch';

/*
  I-1314 (CFC-f5c): the dialog's floors above are px, as the phone's is. In `rem` (`min-h-7`) the
  slide-over sidebar's Close navigation and Sign out were 44 × 87 at the largest text size: a
  floor held up an icon box that a finger's size caps at 44.

  And on a phone a label wraps inside the box that holds it, as it does in a dialog (I-0997): at
  the largest text size Save, Add, Export JSON and Connect a mobile device ran past the window's
  side on eleven settings sections. A label that fits keeps its one line, so nothing moves at the
  default size; a caller that names its own white-space keeps it.
*/
const ON_A_PHONE = 'max-sm:max-w-full max-sm:whitespace-normal';

/*
  I-0997: in a dialog (`ModalFrame` marks its panel `data-modal-panel`) a label wraps rather than run
  past the panel's side. At the largest text size "Transfer ownership" alone was wider than a phone's
  panel. The height becomes a floor, the same one each size had, so a label on one line looks as it
  did; one that has to wrap grows the button instead of spilling out of it. Outside a dialog nothing
  changes: a toolbar's button still keeps its label on one line.
*/
const IN_A_DIALOG = '[[data-modal-panel]_&]';
const inADialog =
  '[[data-modal-panel]_&]:h-auto [[data-modal-panel]_&]:max-w-full [[data-modal-panel]_&]:whitespace-normal [[data-modal-panel]_&]:py-1.5 [[data-modal-panel]_&]:text-center';

/*
  `[&>svg]:shrink-0`: the spinner and a caller's icon keep their size when the label wraps. A wrapping
  label is a flex item that shrinks, and so is an icon beside it, down to a speck (T-0367).
*/
const baseClasses =
  'inline-flex cursor-pointer items-center justify-center whitespace-nowrap font-medium [&>svg]:shrink-0 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 data-[busy]:cursor-wait data-[busy]:opacity-50 dark:focus-visible:ring-offset-[#212121]';

/*
  The CSS property each of Button's own layout utilities sets, so a caller naming the same one wins.
  Only the groups Button sets from `baseClasses` and `sizeClasses` are listed: a caller's utility for
  anything else has nothing here to lose to.
*/
const PROPERTY_GROUPS: Array<[group: string, pattern: RegExp]> = [
  ['display', /^(inline-flex|flex|inline-block|block|inline|grid|inline-grid|hidden|contents)$/],
  ['align-items', /^items-/],
  ['justify-content', /^justify-/],
  ['white-space', /^whitespace-/],
  ['max-width', /^max-w-/],
  ['width', /^(w|min-w|size)-/],
  ['font-weight', /^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/],
  ['height', /^h-/],
  ['gap', /^gap-/],
  ['border-radius', /^rounded(-(none|sm|md|lg|xl|2xl|3xl|full|\[.+\]))?$/],
  ['padding-x', /^(px|p)-/],
  // `text-[length:…]` is a size written as a formula: a bar's cap, `min(0.8125rem,20px)` (I-1307).
  ['font-size', /^text-(xs|sm|base|lg|[2-9]?xl|\[\d[\w.]*\]|\[length:[^\]]+\])$/],
];

/** Where a utility's variants end: its last colon outside brackets (`text-[length:…]` has one inside). */
function variantsEnd(utility: string): number {
  let depth = 0;
  let at = -1;
  for (const [index, character] of [...utility].entries()) {
    if (character === '[') depth += 1;
    else if (character === ']') depth -= 1;
    else if (character === ':' && depth === 0) at = index;
  }
  return at;
}

function splitUtility(utility: string): { variants: string; group: string | null } {
  const at = variantsEnd(utility);
  const variants = at < 0 ? '' : utility.slice(0, at + 1);
  const base = utility.slice(at + 1).replace(/^!/, '');
  return {
    variants,
    group: PROPERTY_GROUPS.find(([, pattern]) => pattern.test(base))?.[0] ?? null,
  };
}

/**
 * Button's own classes without those a caller's `className` overrides (I-0849).
 *
 * Joined by concatenation, the stylesheet's order decided between Button's `whitespace-nowrap` and a
 * caller's `whitespace-normal`, not the caller — so the override did nothing and nothing said so.
 * A caller's utility drops Button's for the same property under the same variants; one without a
 * breakpoint drops Button's at every breakpoint too, because it states the property for every width
 * and Button's `sm:h-7` would otherwise bring the default back from 640 px up.
 *
 * A dialog's own utilities (`inADialog`, I-0997) go to any caller naming the property, at any
 * breakpoint: they out-rank a caller's by specificity, so left in they would win. A caller's height
 * also drops Button's height floors (`min-h-*`: the phone's 44 px and the dialog's), which would
 * otherwise hold it up.
 */
function withoutOverridden(own: string, className: string): string {
  const named = className
    .split(/\s+/)
    .filter(Boolean)
    .map(splitUtility)
    .filter((utility) => utility.group !== null);
  if (named.length === 0) return own;
  return own
    .split(/\s+/)
    .filter((utility) => {
      const mine = splitUtility(utility);
      // Button's own height floor (`min-h-*`) is its height: a caller's height drops it too.
      const group = /^min-h-/.test(utility.slice(utility.lastIndexOf(':') + 1))
        ? 'height'
        : mine.group;
      if (mine.variants.includes(IN_A_DIALOG)) {
        return !named.some((theirs) => theirs.group === group);
      }
      if (group === null) return true;
      return !named.some(
        (theirs) =>
          theirs.group === group && (theirs.variants === mine.variants || theirs.variants === ''),
      );
    })
    .join(' ');
}

/**
 * The classes that make a thing look like a Button, without making it one.
 *
 * Exported because a download has to be an `<a href download>` — a `<button>` cannot hand a file to
 * the browser, and faking it with JavaScript loses the middle-click, the context menu and the
 * keyboard behaviour people already have. Sharing the classes rather than copying them keeps the
 * anchor and the button from drifting apart the next time a hover state changes here.
 */
export function buttonClasses({
  variant = 'primary',
  size = 'md',
  className = '',
}: {
  variant?: Variant;
  size?: Size;
  className?: string;
} = {}): string {
  const own = withoutOverridden(
    `${baseClasses} ${inADialog} ${ON_A_PHONE} ${sizeClasses[size]} ${WIDTH_FLOOR}`,
    className,
  );
  // F-0579 ACC-6: the variant's name, for the highlight Accents › Colour adds in `index.css`. It
  // draws nothing of its own: in Quiet every button is exactly these classes.
  return `${own} ${variantClasses[variant]} ${ACCENT_MARKER}${variant} ${className}`.trim();
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className = '',
  children,
  onClickCapture,
  ...props
}: ButtonProps) {
  /*
    I-2317: a loading button keeps its focus. Natively disabled, the browser moved the focus of the
    button just pressed to the page's body, and a keyboard or screen-reader user started again from
    the top. So it is `aria-disabled` instead, and its press is refused here: a click, Enter or Space
    on it, and Enter in a field of its form (the browser presses the form's submit button for that),
    all reach this capture first, which cancels the press before the caller's `onClick` or the form's
    submit sees it. A button its caller disables stays natively disabled: nothing is pending there.
    `data-busy` dims it, as `busyControlProps` marks any control that waits; never `aria-busy`, which
    a pending gate sets on a control it leaves undimmed.
  */
  const busy = loading && !disabled;
  return (
    <button
      className={buttonClasses({ variant, size, className })}
      disabled={disabled}
      aria-disabled={busy || undefined}
      data-busy={busy || undefined}
      // F-0578 WAIT-11: the inline wait is silent; the button says it is busy.
      aria-busy={loading || undefined}
      {...props}
      onClickCapture={(event) => {
        if (busy) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onClickCapture?.(event);
      }}
    >
      {loading && <Waiting size="inline" />}
      {children}
    </button>
  );
}
